<template>
    <div :id="rootElementId" :style="`height:${svgHeight}px`" class="lollipop-container"></div>
</template>

<script>
import * as d3v4 from "d3v4";
import { round, getDataUrl } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';

export default {
  name: 'CompareLollipopChart',
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false,
    },
    chartData: {
      type: Object,
      required: true,
    },
    radarTemplate:{
      type: String,
      default: ""
    },
    dataPer90MinText: {
      type: String,
      required: false,
    },
    adjustToPossessionText: {
      type: String,
      required: false,
    },
    playerName: {
      type: String,
      required: true,
    },
    radarTitle: {
      type: String,
      required: true,
    },
    yob: {
      type: String,
      required: true,
    },
    minMaxLabel:{
      type: String,
      default: ""
    },
    comparePlayerData: {
      type: Object,
      required: false,
    },
    mediaMode:{
      type: Boolean,
      default: false
    },
    colors:{
      type: Object,
      required:false
    },
    bgColor:{
      type: String,
      required:false
    },
    textColor:{
      type: String,
      required:false
    },
    logoCfg:{
      type: String,
      required:false
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    }
  },
  data() {
    return {
      observer: null,
      images: {},
    }
  },
  computed: {
    darkMode(){
      return this.$vuetify.theme.dark;
    },
    rootElementId(){
      return `lollipop${this.id}`
    },
    svgHeight() {
      const isMobileDevice = this.size < 440 ? 1 : 0;
      const SVG_WIDTH = isMobileDevice ? this.size * 0.9 : this.size * 0.95;
      const SVG_HEIGHT = isMobileDevice ? SVG_WIDTH : SVG_WIDTH * 0.73;
      return SVG_HEIGHT;
    },
    lastSearchId() {
      return this.$store.state.auth.lastSearch;
    }
  },
  async mounted() {
    this.drawChart('#' + this.rootElementId, this.chartData, this.comparePlayerData);
  },
  methods: {
    onResize(){
      let elto = document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    },
    async drawChart(id, chartData, comparePlayerData){
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      //Config data
      let cfg = {
        titleFontSize: 28, //Font size of the labels
        subtitleFontSize: 22, //Font size of the labels
        labelFontSize: 18, //Font size of the labels
        legendFontSize: 17, //Font size of the legend and categories
        legendOffsetY: 20, //Offset Y axis of the legend
        legendOffsetX: 100, //Offset X axis of the legend
        wrapWidthYLabel: 120, // Max Width for labels of Y axis
        spaceLabelAndAxis: 3, //Space between label and the path of Y axis
        color: [this.colors.fillColor, this.colors.comparisonFillColor],	//Color function
        borderColor: [this.colors.borderColor, this.colors.comparisonBorderColor],	//Color function
        heightRect: 43, //Height of upper rects
        widthRect: 20 //Width of upper rects
      };

      // Set the dimensions and margins of the graph
      const isMobileDevice = this.size < 440 ? 1 : 0;
      //1s duration transition between old radar and neew radar
      const duration = 1000;
      const margin = {
        top: isMobileDevice ? (this.size * 0.2) : (this.size * 0.12) > 130 ? 130 : (this.size * 0.12),
        right: isMobileDevice ? (this.size * 0.30) : (this.size * 0.25),
        bottom: isMobileDevice ? (this.size * 0.33) : (this.size * 0.06),
        left: isMobileDevice ? (this.size * 0.29) : (this.size * 0.22) > 200 ? 200 : (this.size * 0.22)
      };

      const SVG_WIDTH = isMobileDevice ? this.size * 0.9 : this.size * 0.95;
      //CHART_WIDTH is svg width without left and right margins
      const CHART_WIDTH = SVG_WIDTH - margin.left - margin.right;
      const SVG_HEIGHT = isMobileDevice ? SVG_WIDTH : SVG_WIDTH * 0.73;
      //CHART_HEIGHT is svg height without top and bottom margins
      const CHART_HEIGHT = isMobileDevice ? SVG_HEIGHT : SVG_HEIGHT - margin.top - margin.bottom;
      const sizePerStat = (CHART_HEIGHT - margin.bottom) / chartData.statDTOs[0].length;
      const ratio = SVG_WIDTH <= 380 ? 0.0025 : SVG_WIDTH < 700 ? 0.0017 : 0.0015;
      const CHART_SIZE_RATIO = (SVG_WIDTH/2) * ratio;
      //Driblab Logo Size
      const logoWidth = SVG_WIDTH * 0.2;
      const logoHeight = SVG_HEIGHT * 0.1;

      //Data
      let playerChartData = chartData.statDTOs[0];
      let comparePlayerChartData = chartData.statDTOs[1];
      let radarPosition = chartData.radarPosition;
      let numberOfPlayers = chartData.numberOfPlayers;
      //Order all stats (Main and Compare Player) by category
      playerChartData.sort((a,b) => (a.categoryOrder > b.categoryOrder) ? 1 : ((b.categoryOrder > a.categoryOrder) ? -1 : 0));
      comparePlayerChartData.sort((a,b) => (a.categoryOrder > b.categoryOrder) ? 1 : ((b.categoryOrder > a.categoryOrder) ? -1 : 0));

      let allStats = playerChartData.map((stat, i) => {
        let compareStat = i < comparePlayerChartData.length ? comparePlayerChartData[i] : comparePlayerChartData[0];
        //If stat is reverse, then ranking = numberOfPlayers - ranking
        let ranking = stat.isReverse ? (numberOfPlayers - (stat.ranking - 1)) : stat.ranking;
        //Main Player percentile value
        let percentileValue = numberOfPlayers > 0 ? (1 - (ranking / numberOfPlayers)) + 0.01 : 0.01;
        percentileValue = percentileValue < 0.01 ? 0.01 : percentileValue > 1 ? 1 : percentileValue;
        //If stat is reverse, then ranking = numberOfPlayers - ranking
        let comparePlayerRanking = compareStat.isReverse ? (numberOfPlayers - (compareStat.ranking - 1)) : compareStat.ranking;
        //Compare Player percentile value
        let percentileCompareValue = numberOfPlayers > 0 ? (1 - (comparePlayerRanking / numberOfPlayers)) + 0.01 : 0.01;
        percentileCompareValue = percentileCompareValue < 0.01 ? 0.01 : percentileCompareValue > 1 ? 1 : percentileCompareValue;

        let valueToShow = (stat.valueToShow > 10 ? round(stat.valueToShow, 1) : round(stat.valueToShow, 2));
        //If value is 0 and is a Ratio Stat (statTypeId = 3), set - as value to show
        if(!valueToShow && stat.statTypeId == 3){
          valueToShow = "-";
        }

        let compareValueToShow = (compareStat.valueToShow > 10 ? round(compareStat.valueToShow, 1) : round(compareStat.valueToShow, 2));
        if(!compareValueToShow && compareStat.statTypeId == 3){
          compareValueToShow = "-";
        }

        return {
          code: stat.code,
          label: (stat.isAdjustedToPossession ? "*" : "") + this.$t('statsNaming.' + stat.code),
          percentileValue: Math.trunc(percentileValue * 100),
          isPercentage: stat.isPercentage,
          category: stat.category,
          ranking: ranking,
          valueToShow: valueToShow,
          isReverse: stat.isReverse,
          tooltip: this.$t('statsNaming.description.' + stat.code),
          //Compare Player values
          percentileCompareValue: Math.trunc(percentileCompareValue * 100),
          compareValueToShow: compareValueToShow,
          comparePlayerRanking: comparePlayerRanking
        }
      });

      let statsByCategories = playerChartData.reduce((accum, stat) => {
        let categoryArray = _.remove(accum, (o) => o.category === stat.category);
        if(categoryArray && categoryArray.length){
          let categoryObj = categoryArray[0];
          categoryObj.total++;
          accum.push(categoryObj);
        }else{
          accum.push({category: stat.category, total: 1, percentileValue: stat.percentileValue});
        }

        return accum;
      },[]);

      //Remove all tooltips
      d3v4.selectAll(".tooltip").remove();
      // Define the div for the tooltip
      let tooltip = d3v4.select("body").append("div")
        .attr("class", "tooltip")
        .style("z-index", "9999")
        .style("display", "none");

      //Recover lines and dots from previous SVG
      let prevSVG = d3v4.select(id).select('svg'),
      mainPlayerCircles = [],
      comparePlayerCircles = [],
      lines = [],
      comparePlayerLines = [],
      values = [],
      comparePlayerValues = [];
      //MAGIC FOR MAKE TRANSITIONS
      if (prevSVG.node()) {
        prevSVG.selectAll(".mainPlayerCircles").each(function(){
          mainPlayerCircles.push(d3v4.select(this).attr("cx"));
        });
        prevSVG.selectAll(".comparePlayerCircles").each(function(){
          comparePlayerCircles.push(d3v4.select(this).attr("cx"));
        });
        prevSVG.selectAll(".mainPlayerLines").each(function(){
          lines.push([d3v4.select(this).attr("x1"), d3v4.select(this).attr("x2")]);
        });
        prevSVG.selectAll(".comparePlayerLines").each(function(){
          comparePlayerLines.push([d3v4.select(this).attr("x1"), d3v4.select(this).attr("x2")]);
        });
        prevSVG.selectAll(".mainPlayerValues").each(function(){
          values.push(d3v4.select(this).attr("x"));
        });
        prevSVG.selectAll(".comparePlayerValues").each(function(){
          comparePlayerValues.push(d3v4.select(this).attr("x"));
        });
        //Remove previous SVG
        prevSVG.remove();
      }
      //END MAGIC

      //-----------------
      // Initialize SVG
      //----------------

      //Initiate the Compare Lollipop chart SVG
      let svg = d3v4.select(id)
        .append("svg")
        .attr("id",`${this.rootElementId}SVG`)
        .attr("width", SVG_WIDTH)
        .attr("height", SVG_HEIGHT)
        .style("background", this.bgColor)
        .append("g")
          .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

      //------------------------------------
      // Draw Title and Number of Players
      //-----------------------------------

      // Draw Blue Rect of Main Player
  		svg.append("rect")
  			.attr("x", - margin.left + 5)
  			.attr("y", isMobileDevice ? -margin.top : -margin.top + 10)
  			.attr("width", cfg.widthRect)
  			.attr("height", cfg.heightRect * (isMobileDevice ? 0.8 : 1))
  			.attr("text-anchor", "left")
        .attr('stroke', cfg.borderColor[0])
        .attr('stroke-width', (3 * CHART_SIZE_RATIO))
  			.style("fill", cfg.color[0]);

      //Draw Black rectangle of Compare Player
    	svg.append("rect")
  			.attr("x", (SVG_WIDTH - margin.left) - cfg.widthRect - 5)
  			.attr("y", isMobileDevice ? -margin.top : -margin.top + 10)
  			.attr("width", cfg.widthRect)
  			.attr("height", cfg.heightRect * (isMobileDevice ? 0.8 : 1))
  			.attr("text-anchor", "right")
        .attr('stroke', cfg.borderColor[1])
        .attr('stroke-width', (3 * CHART_SIZE_RATIO))
  			.style("fill", cfg.color[1]);

      //Year of birth
      let mainPlayerNameText = svg.append("text")
        .attr("x", isMobileDevice ? - margin.left + cfg.widthRect : - margin.left + (50 * CHART_SIZE_RATIO))
        .attr("y", isMobileDevice ? -((margin.top + 80) * CHART_SIZE_RATIO) : -margin.top + 20) //-((margin.top + 25) * CHART_SIZE_RATIO)
        .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("font-weight", "bold")
        .text(this.playerName);

      //Do transition for Main Player Name
      mainPlayerNameText.transition()
      .duration(duration)
      .style("font-size", (cfg.titleFontSize * CHART_SIZE_RATIO) + "px");

      //Add YOB to main player
      let titleYOBMainPlayer = mainPlayerNameText.append("tspan")
        .attr("dx", 0)
        .attr("dy", -(2 * CHART_SIZE_RATIO))
        .style("font-size", (cfg.titleFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("font-weight", "normal")
        .text(this.yob ? "  ('" + this.yob + ")" : ""); //Last 2 digits of Player's Year of Birth

      //Do transition for Year of Birth of Main Player
      titleYOBMainPlayer.transition().duration(duration).style("font-size", (20 * CHART_SIZE_RATIO) + "px");

      //Write Team and Season (or Seasons) text
      let teamAndSeasontext = (isMobileDevice && this.radarTitle.length > 37) ? this.radarTitle.substring(0, 37) + "..." : this.radarTitle;
      let mainPlayerSeasonText = svg.append("text")
        .attr("x", isMobileDevice ? - margin.left + cfg.widthRect : - margin.left + (50 * CHART_SIZE_RATIO))
        .attr("y", isMobileDevice ? -((margin.top + 50) * CHART_SIZE_RATIO) : -margin.top + 35)
        .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .html(teamAndSeasontext);

      //Do transition for Main Player Season Text
      mainPlayerSeasonText.transition().duration(duration).style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px");

      let mainPlayerMinsPlayed = svg.append("text")
        .attr("x", isMobileDevice ? - margin.left + cfg.widthRect : - margin.left + (50 * CHART_SIZE_RATIO))
        .attr("y", isMobileDevice ? -((margin.top + 20) * CHART_SIZE_RATIO) : -margin.top + 50)
        .style("font-size", (7 * CHART_SIZE_RATIO)  + "px")
        .style("font-family", this.fontFamily)
        .text(chartData.minutesPlayed + " mins.");

      //Do transition for Main Player Season Text
      mainPlayerMinsPlayed.transition().duration(duration).style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px");


      let titleYOBComparePlayer = svg.append("text")
      .attr("x", (SVG_WIDTH - margin.left) - (50 * CHART_SIZE_RATIO))
      .attr("y", isMobileDevice ? -((margin.top + 80) * CHART_SIZE_RATIO) : -margin.top + 20)
  			.attr("text-anchor", "end")
  			.style("font-size", (7 * CHART_SIZE_RATIO) +  "px")
  			.style("font-family", this.fontFamily)
  			.style("font-weight", "normal")
        .style("color", this.textColor)
        .style("fill",'currentColor')
  			.text(comparePlayerData && comparePlayerData.yob ? "('" + comparePlayerData.yob + ") " : "");

      //Do transition for Year of Birth of Compare Player
      titleYOBComparePlayer.transition().duration(duration).style("font-size", (20 * CHART_SIZE_RATIO) + "px");

    	let comparePlayerNameText =	titleYOBComparePlayer.append("tspan")
    			.attr("dx", 0)
    			.attr("dy", (2 * CHART_SIZE_RATIO))
    			.style("font-size", (7 * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
    			.style("font-weight", "bold")
    			.text(comparePlayerData ? comparePlayerData.playerName : "");
      //Do transition for Compare Player Name
      comparePlayerNameText.transition().duration(duration).style("font-size", (cfg.titleFontSize * CHART_SIZE_RATIO) + "px");

      let comparePlayerSeasonText = svg.append("text")
          .attr("x", (SVG_WIDTH - margin.left) - (50 * CHART_SIZE_RATIO))
          .attr("y", isMobileDevice ? -((margin.top + 50) * CHART_SIZE_RATIO) : -margin.top + 35)
          .attr("text-anchor", "end")
          .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .text(comparePlayerData ? comparePlayerData.seasonsName : "");
      //Do transition for Main Player Season Text
      comparePlayerSeasonText.transition().duration(duration).style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px");

      let minsPlayedComparePlayer = comparePlayerData ? comparePlayerData.minsPlayed + " mins." : "";
      if(comparePlayerData && comparePlayerData.currentMatchDay){
        minsPlayedComparePlayer = comparePlayerData.currentMatchDay + " " + this.$t('playerRecruitment.radarDescription.basicStatistics.games');
      }

      let comparePlayerMinsPlayedText =  svg.append("text")
          .attr("x", (SVG_WIDTH - margin.left) - (50 * CHART_SIZE_RATIO))
          .attr("y", isMobileDevice ? -((margin.top + 20) * CHART_SIZE_RATIO) : -margin.top + 50)
          .attr("text-anchor", "end")
          .style("font-size", (7 * CHART_SIZE_RATIO)  + "px")
          .style("font-family", this.fontFamily)
          .text(minsPlayedComparePlayer);

      //Do transition for Main Player Mins Played Text
      comparePlayerMinsPlayedText.transition().duration(duration).style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px");

      if(numberOfPlayers > 0){
        let leagueName = " · " + this.minMaxLabel;
        //Write top right number of position player (E.g: 152 Midfielders)
        svg.append("text")
          .attr("x", isMobileDevice ? (SVG_WIDTH - margin.left) : CHART_WIDTH + margin.right - 10)
          .attr("y", isMobileDevice ? -(margin.top * CHART_SIZE_RATIO) + 10 : 0)
          .attr("text-anchor", "end")
          .style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("color", this.darkMode ? "#BCB6B6" : "gray")
          .style("font-weight", "bold")
          .text(numberOfPlayers + " " + this.$t("positions." + radarPosition + "s") + leagueName);

      }

      //------------------------------
      // Draw Compare Lollipop Chart
      //--------------------------------

      //Gray Dashed Lines (For Categories)
      svg.selectAll("myline")
        .data(statsByCategories)
        .enter()
        .append("line")
          .attr("x1", - margin.left + 5)
          .attr("x2", "0")
          .attr("y1", (category, i) => {
            let previousTotal = 0;
            for(let j = 0; j < i; j++){
              previousTotal += statsByCategories[j].total;
            }
            let yCoord = isMobileDevice ? ((previousTotal * 1.1) * sizePerStat) - 20 : (previousTotal * sizePerStat);
            return yCoord;
          })
          .attr("y2", (category, i) => {
            let previousTotal = 0;
            for(let j = 0; j < i; j++){
              previousTotal += statsByCategories[j].total;
            }
            let yCoord = isMobileDevice ? ((previousTotal * 1.1) * sizePerStat) - 20 : (previousTotal * sizePerStat);
            return yCoord;
          })
          .attr("stroke", this.darkMode ? "#BCB6B6" : "gray")
          .attr("stroke-width", "2")
          .style("stroke-dasharray", ("10, 5"));

      //Write Categories (text over dashed lines)
      svg.selectAll("myline")
        .data(statsByCategories)
        .enter()
        .append("text")
        .attr("x", - margin.left + 5)
        .attr("y", (cat, i) => {
          let offset = isMobileDevice ? 22 : 3;
          let previousTotal = 0;
          for(let j = 0; j < i; j++){
            previousTotal += statsByCategories[j].total;
          }
          let yCoord = isMobileDevice ? ((previousTotal * 1.1) * sizePerStat) - offset : (previousTotal * sizePerStat) - offset;
          return yCoord;
        })
        .attr("text-anchor", "start")
        .style("font-size", (cfg.legendFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.darkMode ? "#BCB6B6" : "#616161")
        .style("fill",'currentColor')
        .html((cat) => { return this.$t("faq.metrics.categories." + cat.category)});

      // Create scale for X axis
      const scaleX = d3v4.scaleLinear()
        .domain([0, 100])
        .range([ 0, CHART_WIDTH]);

      const axisX = d3v4.axisBottom(scaleX).tickFormat(function(d,i) {return "p" + d});

      svg.append("g")
        .attr("class", this.darkMode ? "axisDark" : "")
        .attr("transform", "translate(0," + (CHART_HEIGHT - margin.bottom) + ")")
        .call(axisX)
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family",this.fontFamily)

      // Create scale for Y axis
      let axisYrange = isMobileDevice ? [-20, (SVG_HEIGHT * CHART_SIZE_RATIO) + margin.top + 2] : [0, CHART_HEIGHT - margin.bottom];
      const scaleY = d3v4.scaleBand()
        .range(axisYrange)
        .domain(allStats.map(function(d) { return d.label; }));

      const axisY = d3v4.axisLeft(scaleY).tickSizeOuter(0).tickSize(0);

      svg.append("g")
        .attr("class", this.darkMode ? "axisDark" : "")
        .attr("transform", "translate(0,0)")
        .call(axisY)
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family",this.fontFamily)
        .selectAll(".tick text")
        .attr("x", isMobileDevice ? -(cfg.spaceLabelAndAxis * 0.6) : -cfg.spaceLabelAndAxis)
        .call(this.wrap, isMobileDevice ? (cfg.wrapWidthYLabel * 0.6) : cfg.wrapWidthYLabel);

      //Draw Main Player Lines
      svg.selectAll("mainPlayerLines")
        .data(allStats)
        .enter()
        .append("line")
          .attr("class", "mainPlayerLines")
          .attr("x1", function(d, i) {
            if(lines[i]){
              return lines[i][0];
            }
            if(d.percentileValue > d.percentileCompareValue){
              return scaleX(d.percentileCompareValue);
            }else{
              return 1;
            }
          })
          .attr("x2", function(d, i) {
            if(lines[i]){
              return lines[i][1];
            }
            return scaleX(d.percentileValue);
          })
          .attr("y1", function(d) { return scaleY(d.label) + (sizePerStat/2); })
          .attr("y2", function(d) { return scaleY(d.label) + (sizePerStat/2); })
          .attr("stroke", cfg.color[0])
          .attr("stroke-width", function(d) {
            let strokeSize = 7;
            if(d.percentileValue < d.percentileCompareValue){
              strokeSize = 3;
            }
            return (strokeSize * CHART_SIZE_RATIO)
          })
          .transition()
          .duration(duration)
          .attr("x1", function(d) {
            if(d.percentileValue > d.percentileCompareValue){
              return scaleX(d.percentileCompareValue);
            }else{
              return 1;
            }
          })
          .attr("x2", function(d) {
            return scaleX(d.percentileValue);
          });

        //Draw Compare Player Lines
        svg.selectAll("comparePlayerLines")
          .data(allStats)
          .enter()
          .append("line")
            .attr("class", "comparePlayerLines")
            .attr("x1", function(d, i) {
              if(comparePlayerLines[i]){
                return comparePlayerLines[i][0];
              }
              if(d.percentileCompareValue > d.percentileValue){
                return scaleX(d.percentileValue);
              }else{
                return 1;
              }
            })
            .attr("x2", function(d, i) {
              if(comparePlayerLines[i]){
                return comparePlayerLines[i][1];
              }
              return scaleX(d.percentileCompareValue);
            })
            .attr("y1", function(d) { return scaleY(d.label) + (sizePerStat/2); })
            .attr("y2", function(d) { return scaleY(d.label) + (sizePerStat/2); })
            .attr("stroke", cfg.color[1])
            .attr("stroke-width", function(d) {
              let strokeSize = 7;
              if(d.percentileCompareValue < d.percentileValue){
                strokeSize = 3;
              }else if(d.percentileValue === d.percentileCompareValue){
                if(d.valueToShow > d.compareValueToShow){
                  //If value of main player is greather than value of compare player, then gray line doesn't draw
                  strokeSize = 0;
                }
              }
              return (strokeSize * CHART_SIZE_RATIO)
            })
            .transition()
            .duration(duration)
            .attr("x1", function(d) {
              if(d.percentileCompareValue > d.percentileValue){
                return scaleX(d.percentileValue);
              }else{
                return 1;
              }
            })
            .attr("x2", function(d) {
              return scaleX(d.percentileCompareValue);
            });

      var self = this;

      // Circles of Compare Player
      svg.selectAll("comparePlayerCircles")
        .data(allStats)
        .enter()
        .append("circle")
          .attr("class", "comparePlayerCircles")
          .attr("cx", function(d, i) {
            if(comparePlayerCircles[i]){
              return comparePlayerCircles[i];
            }
            let percentileToPrint = d.percentileCompareValue < d.percentileValue ? d.percentileValue : d.percentileCompareValue;
            return scaleX(percentileToPrint);
          })
          .attr("cy", function(d) { return scaleY(d.label) + (sizePerStat/2);})
          .attr("r", function(d) {
            let r = 9;
            //If both circles are at the same percentile and value of main circle is greather than value of compare player, then grey (compare) circle doesn't draw
            if(d.percentileValue === d.percentileCompareValue && d.valueToShow >= d.compareValueToShow){
              r = 0;
            }
            return (r * CHART_SIZE_RATIO);
          })
          .attr("stroke", function(d) {
            let colorIndex = d.percentileCompareValue < d.percentileValue ? 0 : 1;
            return cfg.borderColor[colorIndex];
          })
          .style("fill", function(d) {
            let colorIndex = d.percentileCompareValue < d.percentileValue ? 0 : 1;
            return cfg.color[colorIndex];
          })
          .on('mouseover',function(stat, i) {
            let widthTooltip = 350;
            let lines = Math.ceil(self.measureText(stat.tooltip) / widthTooltip) + 1;
            let yOffset = lines > 4 ? 0 : 20;
            let tooltipY = (event.pageY - lines * 22) - yOffset;
            if(tooltipY < 0){
             //Hide bottom arrow
             d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
             tooltipY = event.pageY + 8;
            }else{
             //Set bottom arrow
             d3.select("body > .tooltip").attr("class",'tooltip');
            }

            let leftOffset = (9 * CHART_SIZE_RATIO);
            d3v4.select("body > .tooltip")
              .transition()
              .duration(200)
              .style("display", "inline-block")
              .style("opacity", 0.95)
              .style("width", widthTooltip + "px")
              .style("height", "auto");

            d3v4.select("body > .tooltip")
              .style("font-size", (17 * CHART_SIZE_RATIO) + "px")
              .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
              .style("left", this.getBoundingClientRect().left - (widthTooltip/2) + leftOffset + "px")
              .style("top", tooltipY + "px")
              .html("<b>" + stat.label + "</b></br>" + stat.tooltip);

            //Increase circle radius on hover
            d3v4.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', (13 * CHART_SIZE_RATIO));

          })
          .on('mouseout',function() {
            //Recover nornmal radius after mouseout
            d3v4.select(this)
              .transition()
              .duration(200)
              .attr('r', (9 * CHART_SIZE_RATIO));

            d3v4.select("body > .tooltip").transition()
              .duration(300)
              .style("opacity", 0);

          })
          .transition()
          .duration(duration)
          .attr("cx", function(d) {
            let percentileToPrint = d.percentileCompareValue < d.percentileValue ? d.percentileValue : d.percentileCompareValue;
            return scaleX(percentileToPrint);
          });


      // Circles of Main Player
      svg.selectAll("mainPlayerCircles")
        .data(allStats)
        .enter()
        .insert("circle", "circle:first-of-type")
          .attr("class", "mainPlayerCircles")
          .attr("cx", function(d, i) {
            if(mainPlayerCircles[i]){
              return mainPlayerCircles[i];
            }
            let percentileToPrint = d.percentileValue <= d.percentileCompareValue ? d.percentileValue : d.percentileCompareValue;
            return scaleX(percentileToPrint);
          })
          .attr("cy", function(d) { return scaleY(d.label) + (sizePerStat/2);})
          .attr("r", function(d) {
            let r = 9;
            //If both circles are at the same percentile and value of main circle is less than value of compare player, then blue (main) circle doesn't draw
            if(d.percentileValue === d.percentileCompareValue && d.valueToShow < d.compareValueToShow){
              r = 0;
            }
            return (r * CHART_SIZE_RATIO);
          })
          .attr("stroke", function(d) {
            let colorIndex = d.percentileValue <= d.percentileCompareValue ? 0 : 1;
            return cfg.borderColor[colorIndex];
          })
          .style("fill", function(d) {
            let colorIndex = d.percentileValue <= d.percentileCompareValue ? 0 : 1;
            return cfg.color[colorIndex];
          })
          .on('mouseover',function(stat, i) {
            let widthTooltip = 350;
            let lines = Math.ceil(self.measureText(stat.tooltip) / widthTooltip) + 1;
            let yOffset = lines > 4 ? 0 : 20;
            let tooltipY = (event.pageY - lines * 22) - yOffset;
            if(tooltipY < 0){
             //Hide bottom arrow
             d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
             tooltipY = event.pageY + 8;
            }else{
             //Set bottom arrow
             d3.select("body > .tooltip").attr("class",'tooltip');
            }

            let leftOffset = (9 * CHART_SIZE_RATIO);
            d3v4.select("body > .tooltip")
              .transition()
              .duration(200)
              .style("display", "inline-block")
              .style("opacity", 0.95)
              .style("width", widthTooltip + "px")
              .style("height", "auto");

            d3v4.select("body > .tooltip")
              .style("font-size", (17 * CHART_SIZE_RATIO) + "px")
              .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
              .style("left", this.getBoundingClientRect().left - (widthTooltip/2) + leftOffset + "px")
              .style("top", tooltipY + "px")
              .html("<b>" + stat.label + "</b></br>" + stat.tooltip);

            //Increase circle radius on hover
            d3v4.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', (13 * CHART_SIZE_RATIO));

          })
          .on('mouseout',function() {
            //Recover nornmal radius after mouseout
            d3v4.select(this)
              .transition()
              .duration(200)
              .attr('r', (9 * CHART_SIZE_RATIO));

            d3v4.select("body > .tooltip").transition()
              .duration(300)
              .style("opacity", 0);

          })
          .transition()
          .duration(duration)
          .attr("cx", function(d) {
            let percentileToPrint = d.percentileValue <= d.percentileCompareValue ? d.percentileValue : d.percentileCompareValue;
            return scaleX(percentileToPrint);
          });

      // Value over Circles of Main player
      let circleValuesMainPlayer = svg.selectAll("mainPlayerValues")
        .data(allStats)
        .enter()
        .append("text")
          .attr("class", "mainPlayerValues")
          .attr("x", function(d, i) {
            if(values[i]){
              return values[i];
            }
            let xCoordCircleMainPlayer = 15;
            if(d.percentileValue < 5){
              //Circle is close to Y Axis
              if(d.valueToShow >= d.compareValueToShow){
                xCoordCircleMainPlayer = d.isReverse ? -5 : -15;
              }else{
                xCoordCircleMainPlayer = d.isReverse ? -15 : -5;
              }
            }else{
              //In this case need to set numers right side
              if(d.valueToShow >= d.compareValueToShow){
                if(d.isReverse){
                  xCoordCircleMainPlayer = 10;
                }else{
                  //In this case is on the right side of the circle
                  return scaleX(d.percentileValue) + 10;
                }
              }else{
                xCoordCircleMainPlayer = d.isReverse ? -15 : 10;
              }
            }
            return scaleX(d.percentileValue) - (xCoordCircleMainPlayer * CHART_SIZE_RATIO);
          })
          .attr("y", function(d) {
            let yCoordCircleMainPlayer = 25;
            if(d.percentileValue < 5){
              //Circle is close to Y Axis
              if(d.valueToShow >= d.compareValueToShow){
                yCoordCircleMainPlayer = d.isReverse ? 10 : 25;
              }else{
                yCoordCircleMainPlayer = d.isReverse ? 25 : 10;
              }
            }else{
              if(d.valueToShow >= d.compareValueToShow){
                if(d.isReverse){
                  yCoordCircleMainPlayer = 10;
                }else{
                  //In this case is on the right side of the circle
                  return scaleY(d.label) + (sizePerStat/2) + (2/CHART_SIZE_RATIO);
                }
              }else{
                yCoordCircleMainPlayer = d.isReverse ? 25 : 10;
              }
            }
            return scaleY(d.label) + (yCoordCircleMainPlayer * CHART_SIZE_RATIO);
          })
          .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("font-weight", "bold")
          .style("fill", this.darkMode ? cfg.color[0] : cfg.borderColor[0])
          .html((stat) => { return stat.valueToShow + (stat.isPercentage ? "%" : "") });

      //Do transition for values close to circles of main player
      circleValuesMainPlayer
        .transition()
        .duration(duration)
        .attr("x", function(d) {
          let xCoordCircleMainPlayer = 15;
          if(d.percentileValue < 5){
            //Circle is close to Y Axis
            if(d.valueToShow >= d.compareValueToShow){
              xCoordCircleMainPlayer = d.isReverse ? -5 : -15;
            }else{
              xCoordCircleMainPlayer = d.isReverse ? -15 : -5;
            }
          }else{
            //In this case need to set numers right side
            if(d.valueToShow >= d.compareValueToShow){
              if(d.isReverse){
                xCoordCircleMainPlayer = 10;
              }else{
                //In this case is on the right side of the circle
                return scaleX(d.percentileValue) + 10;
              }
            }else{
              xCoordCircleMainPlayer = d.isReverse ? -15 : 10;
            }
          }
          return scaleX(d.percentileValue) - (xCoordCircleMainPlayer * CHART_SIZE_RATIO);
        });

      // Value over Circles of Compare player
      let circleValuesComparePlayer = svg.selectAll("comparePlayerValues")
        .data(allStats)
        .enter()
        .append("text")
          .attr("class", "comparePlayerValues")
          .attr("x", function(d, i) {
            if(comparePlayerValues[i]){
              return comparePlayerValues[i];
            }
            let xCoordCircleMainPlayer = 15;
            if(d.percentileCompareValue < 5){
              //Circle is close to Y Axis
              if(d.valueToShow < d.compareValueToShow){
                xCoordCircleMainPlayer = d.isReverse ? -5 : -15;
              }else{
                xCoordCircleMainPlayer = d.isReverse ? -15 : -5;
              }
            }else{
              //In this case need to set numers right side
              if(d.valueToShow < d.compareValueToShow){
                if(d.isReverse){
                  xCoordCircleMainPlayer = 10;
                }else{
                  //In this case is on the right side of the circle
                  return scaleX(d.percentileCompareValue) + 10;
                }
              }else{
                xCoordCircleMainPlayer = d.isReverse ? -15 : 10;
              }
            }
            return scaleX(d.percentileCompareValue) - (xCoordCircleMainPlayer * CHART_SIZE_RATIO);
          })
          .attr("y", function(d) {
            let yCoordCircleMainPlayer = 10;
            if(d.percentileCompareValue < 5){
              //Circle is close to Y Axis
              if(d.valueToShow < d.compareValueToShow){
                yCoordCircleMainPlayer = d.isReverse ? 10 : 25;
              }else{
                yCoordCircleMainPlayer = d.isReverse ? 25 : 10;
              }
            }else{
              if(d.valueToShow < d.compareValueToShow){
                if(d.isReverse){
                  yCoordCircleMainPlayer = 10;
                }else{
                  //In this case is on the right side of the circle
                  return scaleY(d.label) + (sizePerStat/2) + (2/CHART_SIZE_RATIO);
                }
              }else{
                if(d.isReverse){
                  //In this case is on the right side of the circle
                  return scaleY(d.label) + (sizePerStat/2) + (2/CHART_SIZE_RATIO);
                }else{
                  yCoordCircleMainPlayer = 10;
                }
              }
            }
            return scaleY(d.label) + (yCoordCircleMainPlayer * CHART_SIZE_RATIO);
          })
          .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("font-weight", "bold")
          .html((stat) => { return stat.compareValueToShow + (stat.isPercentage ? "%" : "") });


      //Do transition for values close to circles of main player
      circleValuesComparePlayer
        .transition()
        .duration(duration)
        .attr("x", function(d) {
          let xCoordCircleMainPlayer = 15;
          if(d.percentileCompareValue < 5){
            //Circle is close to Y Axis
            if(d.valueToShow < d.compareValueToShow){
              xCoordCircleMainPlayer = d.isReverse ? -5 : -15;
            }else{
              xCoordCircleMainPlayer = d.isReverse ? -15 : -5;
            }
          }else{
            //In this case need to set numers right side
            if(d.valueToShow < d.compareValueToShow){
              if(d.isReverse){
                xCoordCircleMainPlayer = 10;
              }else{
                //In this case is on the right side of the circle
                return scaleX(d.percentileCompareValue) + 10;
              }
            }else{
              xCoordCircleMainPlayer = d.isReverse ? -15 : 10;
            }
          }
          return scaleX(d.percentileCompareValue) - (xCoordCircleMainPlayer * CHART_SIZE_RATIO);
        });

      if(numberOfPlayers > 0){
        // Main Player Right text #Position (Percentile)
        svg.selectAll("mycircle")
          .data(allStats)
          .enter()
          .append("text")
            .attr("x", isMobileDevice ? (SVG_WIDTH - margin.left) : CHART_WIDTH + margin.right - 10)
            .attr("y", function(d) { return scaleY(d.label) + (28 * CHART_SIZE_RATIO);})
            .attr("text-anchor", "end")
            .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
            .style("font-family", this.fontFamily)
            .style("font-weight", "bold")
            .style("fill", this.darkMode ? cfg.color[0] : cfg.borderColor[0])
            .html((stat) => { return "#" + stat.ranking + " (P" + stat.percentileValue + ")" })
            .append("tspan")
            .style("font-family", this.fontFamily)
            .style("fill", this.darkMode ? this.textColor : cfg.borderColor[1])
      			.html((stat) => { return " vs #" + stat.comparePlayerRanking + " (P" + stat.percentileCompareValue + ")"});
      }

      //--------------------------
      // END Draw Lollipop Chart
      //--------------------------

      //-----------------------
      // Draw Bottom Left Text
      //-----------------------
      svg.append("text")
        .attr("x",- margin.left + 5)
        .attr("y", isMobileDevice ? ((SVG_HEIGHT + (margin.bottom * 2.2)) * CHART_SIZE_RATIO) : CHART_HEIGHT)
        .attr("text-anchor", "start")
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color",this.textColor)
        .style("fill",'currentColor')
        .text(this.dataPer90MinText);

      svg.append("text")
        .attr("x", - margin.left + 5)
        .attr("y", isMobileDevice ? ((SVG_HEIGHT + (margin.bottom * 2.2) + 20) * CHART_SIZE_RATIO) : CHART_HEIGHT + 20)
        .attr("text-anchor", "start")
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.textColor)
        .style("fill",'currentColor')
        .text(this.adjustToPossessionText);

      //-------------------
      // Draw Driblab Logo
      //------------------
      if(!this.mediaMode){
        let base_image = new Image();
        let xLogo = isMobileDevice ? CHART_WIDTH - margin.left/8 : CHART_WIDTH + margin.right/6;
        let yLogo = isMobileDevice ? CHART_HEIGHT - margin.top - logoHeight : (CHART_HEIGHT - margin.bottom/2);
        base_image.src = this.mediaMode ? require(this.logoCfg === 'white' ? "@/assets/img/driblab_normal_white.png" : "@/assets/img/driblab_normal_color.png") :
         require((this.logoCfg === 'white' || this.darkMode) ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png");
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(
          svg,
          xLogo,
          yLogo,
          logoWidth,
          logoHeight,
          dataUrl,
          `${this.mediaMode}|${this.logoCfg}`
          );
        }
      }

      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);

      UserService.putLastSearchRendered({id:this.lastSearchId});
    },
    drawImage(svg, x, y, width, height, img, id) {
      if(!this.images[id]){
        let imgNode = svg.append("svg:image")
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[id] = imgNode.node().cloneNode(true);
      }else{
        svg.node().appendChild(this.images[id]);
      }
    },
    //Wraps SVG text
  	wrap(text, width) {
  	  text.each(function() {
    		var text = d3.select(this),
    			words = text.text().split(/\s+/).reverse(),
    			word,
    			line = [],
    			lineNumber = 0,
    			lineHeight = 1.2, // ems
    			y = text.attr("y"),
    			x = text.attr("x"),
    			dy = parseFloat(text.attr("dy")),
    			tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

    		while (word = words.pop()) {
    		  line.push(word);
    		  tspan.text(line.join(" "));
    		  if (tspan.node().getComputedTextLength() > width) {
    				line.pop();
    				tspan.text(line.join(" "));
    				line = [word];
    				tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
    		  }
    		}
  	  });
  	},
    /* This function returns the size in pixels of the string */
  	measureText: function(string, fontSize = 10) {
  	  const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
  			0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
  			0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
  			0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
  			0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
  			0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
  			0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
  			0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
  	  const avg = 0.5293256578947368;

  	  return string
  	    .split('')
  	    .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
  	    .reduce((cur, acc) => acc + cur) * fontSize;
  	},
  },
  destroyed() {
    d3.select(`#${this.rootElementId}`).select("svg").remove();
    delete this.observer;
  },
}

</script>
<style lang="scss">
</style>
